'use client';

/**
 * KnowledgeBaseResultList
 *
 * Renders the structured payload returned by `search_knowledge_base`.
 * Glass list rows with title, snippet (2-line clamp), and score chip.
 *
 * Issue #1432 Phase 2 — Wave 3 P2
 */

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { z } from '@ops-matrix/shared-types';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { transitionPresets } from '../../theme/transitions';
import { GlassChip } from '../GlassChip';
import { RendererEmptyState } from './RendererEmptyState';
import type { AstorToolRendererProps } from './types';

// ============================================================================
// Schema
// ============================================================================

const KbResultSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  snippet: z.string().optional(),
  score: z.number().optional(),
  url: z.string().optional(),
});

type KbResult = z.infer<typeof KbResultSchema>;

const KbDataSchema = z.object({
  results: z.array(KbResultSchema),
});

const KbArraySchema = z.array(KbResultSchema);

function parseKbData(data: unknown): KbResult[] | null {
  const wrapped = KbDataSchema.safeParse(data);
  if (wrapped.success) return wrapped.data.results;

  const bareArray = KbArraySchema.safeParse(data);
  if (bareArray.success) return bareArray.data;

  return null;
}

// ============================================================================
// Component
// ============================================================================

export const KnowledgeBaseResultList = ({ data, onNavigate }: AstorToolRendererProps): ReactElement => {
  const { t } = useI18n();
  const results = useMemo(() => parseKbData(data), [data]);

  if (results === null) {
    return (
      <RendererEmptyState
        title={t('astorTools.error.parseFailed')}
        description={t('astorTools.error.parseFailedDescription')}
      />
    );
  }

  if (results.length === 0) {
    return (
      <RendererEmptyState
        title={t('astorTools.knowledgeBase.empty')}
        description={t('astorTools.knowledgeBase.emptyDescription')}
      />
    );
  }

  return (
    <Stack spacing={1} sx={{ my: 1.5 }}>
      {results.map((result, index) => {
        const key = result.id ?? `${index}-${result.title}`;
        const clickable = Boolean(result.url && onNavigate);
        const handleClick = () => {
          if (clickable && result.url && onNavigate) {
            onNavigate(result.url);
          }
        };
        const handleKeyDown = (event: React.KeyboardEvent) => {
          if (clickable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            handleClick();
          }
        };

        return (
          <Box
            key={key}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? handleClick : undefined}
            onKeyDown={clickable ? handleKeyDown : undefined}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              cursor: clickable ? 'pointer' : 'default',
              transition: transitionPresets.card,
              ...(clickable && {
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                {result.title}
              </Typography>
              {typeof result.score === 'number' && (
                <GlassChip
                  label={`${Math.round(result.score * 100)}% ${t('astorTools.knowledgeBase.score')}`}
                  color="info"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            {result.snippet && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {result.snippet}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default KnowledgeBaseResultList;
